import { Button, Dropdown, Form, Menu, Modal, Table, message } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination, { PaginationProps } from "antd/es/pagination";
import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import moment from "moment";
import ALCModalForm from "./ALCModalForm";
import { Actions, ContributorType, PageSize } from "../../shared/constant";
import { RootState } from "../../redux/rootReducer";
import {
  addContributor,
  deleteContributor,
  editContributor,
  resetContributors,
  retrieveContributors,
} from "../../redux/actions/contributorActions";
import validateMessages from "../../utils/validate";
import CatalogHeader from "./CatalogHeader";
import globalUtils from "../../utils/globalUtils";
import {
  ArtistColumn,
  ComposerColumn,
  LyricistColumn,
} from "../../shared/table/columns";
import tableUtils from "../../utils/tableUtils";
import SearchComponent from "./SearchComponent";
import icons from "../../shared/icons";
import { isClapAdminORClapManager } from "../../utils/clapUtils";

function ContributorPage({ contributorType }: { contributorType: string }) {
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  // const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [effect, setEffect] = useState(false);
  const [mode, setMode] = useState("");
  const [editId, setEditId] = useState("");
  const [file, setFile] = useState(null);
  const [contributorForm] = Form.useForm();
  const {
    artists,
    artistCount,
    lyricists,
    lyricistCount,
    composers,
    composerCount,
    pending,
    fulfilled,
    contributorsPending,
  } = useSelector((state: RootState) => state.contributor);
  const { userDetails } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    dispatch(resetContributors());
  }, []);

  useEffect(() => {
    dispatch(
      retrieveContributors({
        type: contributorType,
        page,
        searchText,
      }),
    );
  }, [page, effect, dispatch, searchText]);

  const hideModal = () => {
    setFile(null);
    setVisible(false);
    contributorForm.resetFields();
    setMode("");
  };

  const getContributorsListData = (type: string) => {
    if (type === ContributorType.ARTIST) return artists;
    if (type === ContributorType.LYRICIST) return lyricists;
    return composers;
  };

  useEffect(() => {
    if (fulfilled) {
      /* Create and Edit success */
      if (mode === Actions.CREATE || mode === Actions.EDIT) {
        setEffect(!effect);
        hideModal();
      }

      if (mode === Actions.DELETE) {
        message.success(
          `${globalUtils.snakeCaseToCapitalize(
            contributorType,
          )} successfully deleted`,
        );
        setMode("");
        if (getContributorsListData(contributorType).length === 1 && page > 1)
          setPage(page - 1);
        setEffect(!effect);
      }
    }
  }, [pending]);

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  const UploadFile = (content: any) => {
    setFile(content);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
    setEffect(!effect);
  };

  const showModal = () => {
    setMode(Actions.CREATE);
    setVisible(true);
  };

  const onSave = async () => {
    try {
      await contributorForm.validateFields();
    } catch (errorInfo) {
      return;
    }

    const formFields = contributorForm.getFieldsValue(true);
    const formData = new FormData();
    formData.append("name", formFields.name);
    formData.append("country", formFields.country);
    formData.append("email", formFields.email);
    formData.append("phone", formFields.phoneNumber);
    if (formFields.dob)
      formData.append("date_of_birth", moment(formFields.dob).format());
    else formData.append("date_of_birth", "");
    if (file) formData.append("profile_picture", file as unknown as Blob);
    if (mode === Actions.CREATE) formData.append("type", contributorType);

    if (mode === Actions.CREATE) dispatch(addContributor(formData));
    else dispatch(editContributor({ id: editId, body: formData }));
  };

  /*   const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }; */

  const getData = (type: string) => {
    if (type === ContributorType.ARTIST) {
      const data = {
        contributorColumn: ArtistColumn,
        contributorActions: {},
        contributors: artists,
        contributorCount: artistCount,
      };
      return data;
    }
    if (type === ContributorType.LYRICIST) {
      const data = {
        contributorColumn: LyricistColumn,
        contributorActions: {},
        contributors: lyricists,
        contributorCount: lyricistCount,
      };
      return data;
    }
    const data = {
      contributorColumn: ComposerColumn,
      contributorActions: {},
      contributors: composers,
      contributorCount: composerCount,
    };
    return data;
  };

  const onDelete = (record: any) => {
    Modal.confirm({
      title: `Are you sure to delete this ${globalUtils.snakeCaseToCapitalize(
        contributorType,
      )}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        await dispatch(deleteContributor({ id: record.id }));
        setMode(Actions.DELETE);
      },
      onCancel() {},
    });
  };

  const onEdit = (record: any) => {
    setMode(Actions.EDIT);
    setVisible(true);
    setEditId(record.id);
    // note: there is no functionality in status right now
    contributorForm.setFieldsValue({
      name: record.name,
      dob: record.date_of_birth && moment(record.date_of_birth),
      country: record.country,
      email: record.email,
      phoneNumber: record.phone,
    });
  };

  const actions = {
    title: "",
    key: "action",
    align: "right",
    render: (_: any, record: any) =>
      isClapAdminORClapManager(userDetails) && (
        <div className="text-secondary">
          <Dropdown
            overlay={
              <Menu
                className="min-w-[100px] rounded-[3px]"
                items={[
                  {
                    label: (
                      <div onClick={() => onEdit(record)} aria-hidden="true">
                        Edit
                      </div>
                    ),
                    key: "EDIT",
                  },
                  {
                    label: (
                      <div onClick={() => onDelete(record)} aria-hidden="true">
                        Delete
                      </div>
                    ),
                    key: "DELETE",
                  },
                ]}
              />
            }
            trigger={["click"]}
          >
            <button type="button">{icons.DotsVerticle()}</button>
          </Dropdown>
        </div>
      ),
  };

  return (
    <div>
      <CatalogHeader
        title={`${globalUtils.snakeCaseToCapitalize(contributorType)}s`}
        handleClick={showModal}
      />
      <div>
        <Modal
          visible={visible}
          title={`${
            mode === Actions.CREATE ? "Create" : "Edit"
          } ${globalUtils.snakeCaseToCapitalize(contributorType)}`}
          maskClosable={false}
          centered
          width={740}
          onCancel={hideModal}
          destroyOnClose
          footer={[
            <Button key="cancel" onClick={hideModal}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={pending as boolean}
              onClick={onSave}
            >
              Save
            </Button>,
          ]}
        >
          <div className="mx-6 mt-6">
            <Form
              form={contributorForm}
              layout="vertical"
              name="contributorForm"
              validateMessages={validateMessages}
            >
              <ALCModalForm
                modalName={globalUtils.snakeCaseToCapitalize(contributorType)}
                onFileUpload={UploadFile}
              />
            </Form>
          </div>
        </Modal>
      </div>
      <div className="mt-5 mb-4">
        <SearchComponent handleSearch={handleSearch} />
      </div>
      <div>
        <Table
          // rowSelection={
          //   getData(contributorType).contributorCount && rowSelection
          // }
          rowKey={(record: any) => {
            return record.id;
          }}
          columns={tableUtils.buildColumns(
            getData(contributorType).contributors,
            getData(contributorType).contributorColumn,
            actions,
          )}
          dataSource={getData(contributorType).contributors}
          pagination={false}
          size="middle"
          scroll={{
            ...(globalUtils.checkIsScroll(
              getData(contributorType).contributors,
            ) && { y: 474 }),
            x: 1024,
          }}
          className="ant_table"
          loading={{
            spinning: contributorsPending as boolean,
            indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
          }}
        />
      </div>
      <div className="text-right mt-4">
        <Pagination
          current={page}
          pageSize={PageSize.FIFTY}
          onChange={handlePageChange}
          total={getData(contributorType).contributorCount}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
}

export default ContributorPage;
